// @ vendor
const Immutable = require('immutable');
const get = require('lodash/object/get');
const filter = require('lodash/collection/filter');
const trim = require('lodash/string/trim');
// @ reducers
const loansIntervenersReducer = require('../reducers/loans/loanInterveners');
const loansGeneralDataReducer = require('../reducers/loans/loanGeneralData');
// @ utilities
const {
    groupByCurrencyAndCalculateTotally,
    mergeOrderAndId,
    isMortgage,
    isPayroll,
    isPreapproved,
    isRetail,
    isOldLoanType,
    isAres,
    isRestrictedLoanType
} = require('utilities/APIParsingHelper');
const { hash } = require('utilities/hash');
// @ constants
const actionTypes = require('../loansConstants/actionTypes');
const {
    LOANS_MORTGAGES_MORTGAGE_LOAN,
    LOANS_MORTGAGES_PAYROLL_LOAN,
    LOANS_MORTGAGES_PERSONAL_LOAN,
    LOANS_MORTGAGES_PREAPPROVED_LOAN,
    LOANS_MORTGAGES_RETAIL_LOAN,
    LOANS_MORTGAGES_ARES_LOAN
} = require('../loansConstants/index');
const {
    EURO_TEXT,
    FILTERS_VALID,
    API_ENTERPRISE_NUMBER,
    API_CENTER
} = require('constants/index');

const initialState = Immutable.fromJS({
    isFetching: false,
    byId: {},
    error: '',
    loansByOrder: [],
    mortgagesByOrder: [],
    loansBalance: [],
    mortgagesBalance: [],
    loansBalanceForPG: [],
    mortgagesBalanceForPG: []
});

function getLoanType(newProduct, productSubType, productStandard) {
    if (isMortgage(newProduct, productSubType, productStandard))
        return LOANS_MORTGAGES_MORTGAGE_LOAN;
    else if (isPayroll(newProduct, productSubType))
        return LOANS_MORTGAGES_PAYROLL_LOAN;
    else if (isRetail(newProduct, productSubType))
        return LOANS_MORTGAGES_RETAIL_LOAN;
    else if (isPreapproved(newProduct, productSubType))
        return LOANS_MORTGAGES_PREAPPROVED_LOAN;
    else if (isAres(newProduct, productSubType))
        return LOANS_MORTGAGES_ARES_LOAN;
    else
        return LOANS_MORTGAGES_PERSONAL_LOAN;
}

function updateLoansList(data) {
    const loans = get(data, 'payload.datosSalidaPrestamos.prestamos', []);
    let byId = {};
    let mortgagesByOrder = [];
    let loansByOrder = [];

    loans.forEach(loan => {
        const loanId = hash(trim(loan.cnuevo.producto + loan.cnuevo.numerodecontrato));
        const newProduct = get(loan, 'cnuevo.producto', '');
        const productSubType = get(loan, 'criterios.c1.subtipoproducto', '');
        const productStandard = get(loan, 'criterios.c1.estand', '');
        const type = getLoanType(newProduct, productSubType, productStandard);
        const isMortgage = type === LOANS_MORTGAGES_MORTGAGE_LOAN;
        const isOldLoan = isOldLoanType(newProduct, productSubType);
        const isRestrictedLoan = isRestrictedLoanType(newProduct, productSubType);
        // Retail and Payroll act as Personal Loans
        const isPersonal = type === LOANS_MORTGAGES_PERSONAL_LOAN ||
            type === LOANS_MORTGAGES_PAYROLL_LOAN ||
            type === LOANS_MORTGAGES_RETAIL_LOAN ||
            type === LOANS_MORTGAGES_ARES_LOAN;
        const isPreapproved = type === LOANS_MORTGAGES_PREAPPROVED_LOAN;

        byId[loanId] = {
            loanId,
            type,
            newProduct,
            productSubType,
            productStandard,
            isOldLoan,
            isRestrictedLoan,
            isMortgage,
            isPreapproved,
            isPersonal,
            fullContractNumber: `${API_ENTERPRISE_NUMBER}${API_CENTER}${newProduct}${get(loan, 'cnuevo.numerodecontrato')}`,
            openingDate: get(loan, 'criterios.c2.fecsit', ''),
            alias: trim(loan.descripcion),
            aliasError: false,
            aliasTentative: '',
            isAliasUpdating: false,
            balance: {
                amount: get(loan, 'saldoDisponible.importe', 0),
                currency: get(loan, 'saldoDisponible.divisa', EURO_TEXT)
            },
            interventionType: trim(get(loan, 'descIntervenciones.nomTipInterv')),
            contractNumber: get(loan, 'cnuevo.numerodecontrato'),
            product: get(loan, 'cnuevo.producto', ''),
            newContractNumber: get(loan, 'cnuevo.numerodecontrato'),
            filters: {
                block: get(loan, 'filtros.bloque'),
                valid: get(loan, 'filtros.valido') === FILTERS_VALID
            },
            loanHolder: get(loan, 'nombretitular'),
            catalogData: {
                standard: get(loan, 'catalogData.estandar'),
                product: get(loan, 'catalogData.producto'),
                subtype: get(loan, 'catalogData.subtipo')
            },
            account: get(loan, 'cuentaAsociada'),
            interveners: loansIntervenersReducer(undefined, { type: null }),
            generalData: loansGeneralDataReducer(undefined, { type: null })
        };

        if (isMortgage) {
            mortgagesByOrder.push(loanId);
        } else {
            loansByOrder.push(loanId);
        }
    });

    const loansData = mergeOrderAndId(loansByOrder, byId);
    const validLoans = filter(loansData, loan => loan.filters.valid);
    const loansBalance = groupByCurrencyAndCalculateTotally(loansData, 'balance');
    const loansBalanceForPG = groupByCurrencyAndCalculateTotally(validLoans, 'balance');

    const mortgagesData = mergeOrderAndId(mortgagesByOrder, byId);
    const validMortgages = filter(mortgagesData, loan => loan.filters.valid);
    const mortgagesBalance = groupByCurrencyAndCalculateTotally(mortgagesData, 'balance');
    const mortgagesBalanceForPG = groupByCurrencyAndCalculateTotally(validMortgages, 'balance');

    return {
        byId,
        isFetching: false,
        loansByOrder,
        loansBalance,
        loansBalanceForPG,
        mortgagesByOrder,
        mortgagesBalance,
        mortgagesBalanceForPG
    };
}

function loansReducer(state = initialState, action) {
    switch (action.type) {
        case actionTypes.GLOBAL_POSITION_REQUEST_SUCCESS:
            return state.mergeDeep(updateLoansList(action));
        case actionTypes.FETCH_LOAN_INTERVENERS:
        case actionTypes.FETCH_LOAN_INTERVENERS_SUCCESS:
        case actionTypes.FETCH_LOAN_INTERVENERS_FAILURE:
            const intervenersStatePath = ['byId', action.payload.loanId, 'interveners'];
            return state.setIn(
                intervenersStatePath,
                loansIntervenersReducer(state.getIn(intervenersStatePath), action)
            );
        case actionTypes.FETCH_LOAN_GENERAL_DATA:
        case actionTypes.FETCH_LOAN_GENERAL_DATA_FAILURE:
        case actionTypes.FETCH_LOAN_GENERAL_DATA_SUCCESS:
            const generalDataStatePath = ['byId', action.payload.loanId, 'generalData'];
            return state.setIn(
                generalDataStatePath,
                loansGeneralDataReducer(state.getIn(generalDataStatePath), action)
            );
        case actionTypes.SET_LOAN_ALIAS_REQUEST:
            return state.mergeIn(['byId', action.payload.loanId], {
                isAliasUpdating: true,
                aliasTentative: action.payload.alias,
                aliasError: false
            });
        case actionTypes.SET_LOAN_ALIAS_SUCCESS:
            return state.mergeIn(['byId', action.payload.loanId], {
                isAliasUpdating: false,
                alias: action.payload.alias,
                aliasTentative: ''
            });
        case actionTypes.SET_LOAN_ALIAS_FAILURE:
            return state.mergeIn(['byId', action.payload.loanId], {
                isAliasUpdating: false,
                aliasTentative: '',
                aliasError: true
            });
        case actionTypes.SET_PROFILE_EDIT_SETTINGS_LOAN_SUCCESS:
            return state.mergeIn(['byId', action.payload.loanId, 'filters'], {
                valid: action.payload.valid
            });
        case actionTypes.MORTGAGES_STATUS_REQUEST:
            return state.mergeDeep({
                isFetchingMortgagesStatus: true
            });
        case actionTypes.MORTGAGES_STATUS_SUCCESS:
            return state.mergeDeep({
                isFetchingMortgagesStatus: false,
                mortgagesStatus: action.payload.status
            });
        case actionTypes.MORTGAGES_STATUS_FAILURE:
            return state.mergeDeep({
                isFetchingMortgagesStatus: false,
                mortgagesStatus: ''
            });
        default:
            return state;
    }
}

module.exports = loansReducer;
